import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export interface PageHeaderAction {
  label: string;
  disabled?: boolean;
  loading?: boolean;
  variant?: "default" | "success" | "ghost" | string;
  icon?: ReactNode;
  className?: string;
  onClick: () => void;
}

interface BreadcrumbContextType {
  pageTitle: string | null;
  setPageTitle: (title: string | null) => void;
  pageAction: PageHeaderAction | null;
  setPageAction: (action: PageHeaderAction | null) => void;
  pageHeaderContent: ReactNode | null;
  setPageHeaderContent: (content: ReactNode | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(
  undefined
);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [pageTitle, setPageTitleState] = useState<string | null>(null);
  const [pageAction, setPageActionState] = useState<PageHeaderAction | null>(null);
  const [pageHeaderContent, setPageHeaderContent] = useState<ReactNode | null>(
    null
  );
  const onClickRef = useRef<(() => void) | undefined>(undefined);

  const setPageTitle = useCallback((title: string | null) => {
    setPageTitleState((prev) => (prev === title ? prev : title));
  }, []);

  const setPageAction = useCallback((action: PageHeaderAction | null) => {
    if (!action) {
      onClickRef.current = undefined;
      setPageActionState(null);
      return;
    }

    onClickRef.current = action.onClick;

    setPageActionState((prev) => {
      if (
        prev &&
        prev.label === action.label &&
        prev.disabled === action.disabled &&
        prev.loading === action.loading &&
        prev.variant === action.variant
      ) {
        return prev;
      }
      return {
        label: action.label,
        disabled: action.disabled,
        loading: action.loading,
        variant: action.variant,
        icon: action.icon,
        className: action.className,
        onClick: () => onClickRef.current?.(),
      };
    });
  }, []);

  const value = useMemo(
    () => ({
      pageTitle,
      setPageTitle,
      pageAction,
      setPageAction,
      pageHeaderContent,
      setPageHeaderContent,
    }),
    [
      pageTitle,
      setPageTitle,
      pageAction,
      setPageAction,
      pageHeaderContent,
      setPageHeaderContent,
    ]
  );

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumb() {
  const context = useContext(BreadcrumbContext);
  return (
    context ?? {
      pageTitle: null,
      setPageTitle: () => {},
      pageAction: null,
      setPageAction: () => {},
      pageHeaderContent: null,
      setPageHeaderContent: () => {},
    }
  );
}
